import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import { CompanyAddressDto, CompanyDto, MichiganTitleVehicleDto } from 'dtos'
import { useFormikContext } from 'formik'
import { Junction } from 'localConstants'
import { useEffect, useState } from 'react'
import { formatAddress, formatDate, formatDateMoment } from 'utils'
import {
  MICHIGAN_TITLES_DOCUMENT_DOWNLOAD_LABEL,
  michiganTitlesCompactCellSx,
  michiganTitlesCompactSelectProps,
  michiganTitlesCompactTextFieldSx,
  michiganTitlesDateCellSx,
  michiganTitlesDocumentCellSx,
  michiganTitlesLinkSx,
  michiganTitlesMissingCellSx,
  michiganTitlesPartyActionsCellSx,
  michiganTitlesPartyTableMergedSx,
  michiganTitlesRowLabelCellSx,
  michiganTitlesTableContainerSx,
  michiganTitlesTableSx
} from './michiganTitlesTableStyles'

export default function SelectedVehicleCard() {
  const { dirty, errors, handleBlur, handleChange, isSubmitting, setFieldValue, setValues, values } =
    useFormikContext<MichiganTitleVehicleDto>()

  const formatOwnerAddress1 = (address?: CompanyAddressDto) =>
    address ? address.address1 + (address.address2 ? ` ${address.address2}` : '') : undefined

  const applyOwnerFields = (
    fields: Pick<
      MichiganTitleVehicleDto,
      | 'michiganTitleOwnerName'
      | 'michiganTitleOwnerAddress1'
      | 'michiganTitleOwnerCity'
      | 'michiganTitleOwnerCounty'
      | 'michiganTitleOwnerCountry'
      | 'michiganTitleOwnerState'
      | 'michiganTitleOwnerPostalCode'
      | 'michiganTitleOwnerPhone'
      | 'michiganTitleOwnerIrsNumber'
      | 'michiganTitleOwnerDealerNumber'
    >
  ) => {
    setValues(prev => ({ ...prev, ...fields }))
  }

  const [exporterCompanyAddresses, setExporterCompanyAddresses] = useState<CompanyAddressDto[]>([])
  const [isGettingExporterCompanyAddresses, setIsGettingExporterCompanyAddresses] = useState<boolean>(false)
  const [buyerCompanyAddresses, setBuyerCompanyAddresses] = useState<CompanyAddressDto[]>([])
  const [isGettingBuyerCompanyAddresses, setIsGettingBuyerCompanyAddresses] = useState<boolean>(false)
  const [titleDestinationCompanyAddresses, setTitleDestinationCompanyAddresses] = useState<CompanyAddressDto[]>([])
  const [isGettingTitleDestinationCompanyAddresses, setIsGettingTitleDestinationCompanyAddresses] = useState<boolean>(false)
  const [vehicleDestinationCompanyAddresses, setVehicleDestinationCompanyAddresses] = useState<CompanyAddressDto[]>([])
  const [isGettingVehicleDestinationCompanyAddresses, setIsGettingVehicleDestinationCompanyAddresses] = useState<boolean>(false)
  const [registeredImporterCompany, setRegisteredImporterCompany] = useState<CompanyDto | undefined>(undefined)
  const [registeredImporterAddress, setRegisteredImporterAddress] = useState<CompanyAddressDto | undefined>(undefined)

  useEffect(() => {
    controllers.getRegisteredImporterCompany().then(res => {
      setRegisteredImporterCompany(res.value)
    })

    controllers.getRegisteredImporterAddress().then(res => {
      setRegisteredImporterAddress(res.value)
    })
  }, [])

  useEffect(() => {
    if (!values.exporterCompany?.companyGuid) {
      return
    }

    setIsGettingExporterCompanyAddresses(true)
    controllers
      .getCompanyAddresses(values.exporterCompany.companyGuid, {
        page: 0,
        pageSize: 100,
        junction: Junction.AND,
        statuses: ['isActive']
      })
      .then(res => {
        setExporterCompanyAddresses(res.value)
      })
      .finally(() => {
        setIsGettingExporterCompanyAddresses(false)
      })
  }, [values.exporterCompany])

  useEffect(() => {
    if (!values.buyerCompany?.companyGuid) {
      return
    }

    setIsGettingBuyerCompanyAddresses(true)
    controllers
      .getCompanyAddresses(values.buyerCompany.companyGuid, {
        page: 0,
        pageSize: 100,
        junction: Junction.AND,
        statuses: ['isActive', 'isBuyerCompanyAddress']
      })
      .then(res => {
        setBuyerCompanyAddresses(res.value)
      })
      .finally(() => {
        setIsGettingBuyerCompanyAddresses(false)
      })
  }, [values.buyerCompany])

  useEffect(() => {
    if (!values.titleDestinationCompany?.companyGuid) {
      return
    }

    setIsGettingTitleDestinationCompanyAddresses(true)
    controllers
      .getCompanyAddresses(values.titleDestinationCompany.companyGuid, {
        page: 0,
        pageSize: 100,
        junction: Junction.AND,
        statuses: ['isActive', 'isTitleDestinationCompanyAddress']
      })
      .then(res => {
        setTitleDestinationCompanyAddresses(res.value)
      })
      .finally(() => {
        setIsGettingTitleDestinationCompanyAddresses(false)
      })
  }, [values.titleDestinationCompany])

  useEffect(() => {
    if (!values.vehicleDestinationCompany?.companyGuid) {
      return
    }

    setIsGettingVehicleDestinationCompanyAddresses(true)
    controllers
      .getCompanyAddresses(values.vehicleDestinationCompany.companyGuid, {
        page: 0,
        pageSize: 100,
        junction: Junction.AND,
        statuses: ['isActive', 'isVehicleDestinationCompanyAddress']
      })
      .then(res => {
        setVehicleDestinationCompanyAddresses(res.value)
      })
      .finally(() => {
        setIsGettingVehicleDestinationCompanyAddresses(false)
      })
  }, [values.vehicleDestinationCompany])

  return (
    <Card key={values.vehicleGuid}>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Selected Vehicle
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TableContainer sx={michiganTitlesTableContainerSx}>
              <Table size='small' sx={michiganTitlesTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Importer Stock #</TableCell>

                    <TableCell>Exporter Company</TableCell>

                    <TableCell>Buyer Company</TableCell>

                    <TableCell>PAPS #</TableCell>

                    <TableCell>Signed BOL</TableCell>

                    <TableCell>VIN</TableCell>

                    <TableCell>Year</TableCell>

                    <TableCell>Make</TableCell>

                    <TableCell>Model</TableCell>

                    <TableCell>7501</TableCell>

                    <TableCell>CAN Ownership</TableCell>

                    <TableCell>Vehicle Check-In</TableCell>

                    <TableCell>Sent to DOT</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell sx={michiganTitlesCompactCellSx}>{values.importerStockNumber}</TableCell>

                    <TableCell>{values.exporterCompany?.name}</TableCell>

                    <TableCell>{values.buyerCompany?.name ?? '-'}</TableCell>

                    <TableCell sx={michiganTitlesCompactCellSx}>{values.inboundTripPAPS?.papsNumber}</TableCell>

                    <TableCell sx={michiganTitlesDateCellSx}>
                      {values._SignedBOLCreatedDateTime
                        ? new Date(values._SignedBOLCreatedDateTime).toLocaleDateString()
                        : '-'}
                    </TableCell>

                    <TableCell sx={michiganTitlesCompactCellSx}>{values.vin}</TableCell>

                    <TableCell sx={michiganTitlesCompactCellSx}>{values.year}</TableCell>

                    <TableCell>{values.make}</TableCell>

                    <TableCell>{values.model}</TableCell>

                    {values._7501FileName ? (
                      <TableCell sx={michiganTitlesDocumentCellSx}>
                        <Link
                          onClick={() => {
                            controllers.vehicles.getVehicleDocumentFileName(values.vehicleGuid, values._7501FileName!)
                          }}
                          sx={michiganTitlesLinkSx}
                        >
                          {MICHIGAN_TITLES_DOCUMENT_DOWNLOAD_LABEL}
                        </Link>
                      </TableCell>
                    ) : (
                      <TableCell sx={michiganTitlesMissingCellSx}>MISSING</TableCell>
                    )}

                    {values._CANOwnershipFileName ? (
                      <TableCell sx={michiganTitlesDocumentCellSx}>
                        <Link
                          onClick={() => {
                            controllers.vehicles.getVehicleDocumentFileName(values.vehicleGuid, values._CANOwnershipFileName!)
                          }}
                          sx={michiganTitlesLinkSx}
                        >
                          {MICHIGAN_TITLES_DOCUMENT_DOWNLOAD_LABEL}
                        </Link>
                      </TableCell>
                    ) : (
                      <TableCell sx={michiganTitlesMissingCellSx}>MISSING</TableCell>
                    )}

                    <TableCell sx={michiganTitlesDateCellSx}>
                      {values.vehicleCheckInDateTime ? formatDate(values.vehicleCheckInDateTime) : '-'}
                    </TableCell>

                    <TableCell sx={michiganTitlesDateCellSx}>
                      {values.sentToDOTDateTime ? formatDateMoment(values.sentToDOTDateTime) : '-'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} sx={theme => ({ mt: theme.spacing(4) })}>
            <TableContainer sx={michiganTitlesTableContainerSx}>
              <Table size='small' sx={michiganTitlesPartyTableMergedSx}>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>

                    <TableCell>Name</TableCell>

                    <TableCell>Address</TableCell>

                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell sx={michiganTitlesRowLabelCellSx}>CAN Ownership</TableCell>

                  <TableCell>{values.canOwnershipName}</TableCell>

                  <TableCell>{`${values.canOwnershipAddress1 ? values.canOwnershipAddress1 + ',' : ''} ${
                    values.canOwnershipCity ? values.canOwnershipCity + ',' : ''
                  } ${values.canOwnershipProvince ?? ''} ${values.canOwnershipPostalCode ?? ''} ${
                    values.canOwnershipAddress1 ||
                    values.canOwnershipCity ||
                    values.canOwnershipProvince ||
                    values.canOwnershipPostalCode
                      ? 'CA'
                      : ''
                  }`}</TableCell>

                  <TableCell align='right' sx={michiganTitlesPartyActionsCellSx}>
                    <Button
                      color='primary'
                      disabled={
                        !values.canOwnershipName ||
                        !values.canOwnershipAddress1 ||
                        !values.canOwnershipCity ||
                        !values.canOwnershipProvince ||
                        !values.canOwnershipPostalCode
                      }
                      onClick={() => {
                        setFieldValue('michiganTitleSpecialMailerName', values.canOwnershipName)
                        setFieldValue('michiganTitleSpecialMailerAddress1', values.canOwnershipAddress1)
                        setFieldValue('michiganTitleSpecialMailerCity', values.canOwnershipCity)
                        setFieldValue('michiganTitleSpecialMailerState', values.canOwnershipProvince)
                        setFieldValue('michiganTitleSpecialMailerPostalCode', values.canOwnershipPostalCode)
                      }}
                      size='small'
                      variant='text'
                      sx={theme => ({ mr: theme.spacing(4) })}
                    >
                      ADD AS SPECIAL MAILER
                    </Button>

                    <Button
                      color='primary'
                      disabled={
                        !values.canOwnershipName ||
                        !values.canOwnershipAddress1 ||
                        !values.canOwnershipCity ||
                        !values.canOwnershipProvince ||
                        !values.canOwnershipPostalCode
                      }
                      onClick={() => {
                        applyOwnerFields({
                          michiganTitleOwnerName: values.canOwnershipName,
                          michiganTitleOwnerAddress1: values.canOwnershipAddress1,
                          michiganTitleOwnerCity: values.canOwnershipCity,
                          michiganTitleOwnerCounty: '',
                          michiganTitleOwnerCountry: 'CA',
                          michiganTitleOwnerState: values.canOwnershipProvince,
                          michiganTitleOwnerPostalCode: values.canOwnershipPostalCode
                        })
                      }}
                      size='small'
                      variant='text'
                    >
                      ADD AS OWNER
                    </Button>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={michiganTitlesRowLabelCellSx}>Exporter Company</TableCell>

                  <TableCell>{values.exporterCompany?.name}</TableCell>

                  <TableCell>
                    <TextField
                      disabled={isGettingExporterCompanyAddresses}
                      fullWidth
                      InputProps={{
                        endAdornment: isGettingBuyerCompanyAddresses ? (
                          <InputAdornment position='end' sx={{ mr: 2.4 }}>
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ) : (
                          <></>
                        )
                      }}
                      onChange={e => {
                        setFieldValue(
                          'michiganTitleExporterCompanyAddress',
                          exporterCompanyAddresses.filter(
                            exporterCompanyAddress => exporterCompanyAddress.addressGuid === e.target.value
                          )[0]
                        )
                      }}
                      margin='dense'
                      select
                      SelectProps={michiganTitlesCompactSelectProps}
                      size='small'
                      sx={michiganTitlesCompactTextFieldSx}
                      value={values.michiganTitleExporterCompanyAddress?.addressGuid}
                    >
                      {exporterCompanyAddresses.map(exporterCompanyAddress => (
                        <MenuItem key={exporterCompanyAddress.addressGuid} value={exporterCompanyAddress.addressGuid}>
                          {formatAddress(exporterCompanyAddress, false)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell align='right' sx={michiganTitlesPartyActionsCellSx}>
                    <Button
                      color='primary'
                      disabled={!values.exporterCompany || !values.michiganTitleExporterCompanyAddress}
                      onClick={() => {
                        setFieldValue('michiganTitleSpecialMailerName', values.exporterCompany?.name)

                        setFieldValue(
                          'michiganTitleSpecialMailerAddress1',
                          values.michiganTitleExporterCompanyAddress?.address1 +
                            (values.michiganTitleExporterCompanyAddress?.address2
                              ? ` ${values.michiganTitleExporterCompanyAddress?.address2}`
                              : '')
                        )

                        setFieldValue('michiganTitleSpecialMailerCity', values.michiganTitleExporterCompanyAddress?.city)

                        setFieldValue(
                          'michiganTitleSpecialMailerState',
                          values.michiganTitleExporterCompanyAddress?.stateProvince
                        )

                        setFieldValue(
                          'michiganTitleSpecialMailerPostalCode',
                          values.michiganTitleExporterCompanyAddress?.postalCode
                        )
                      }}
                      size='small'
                      variant='text'
                      sx={theme => ({ mr: theme.spacing(4) })}
                    >
                      ADD AS SPECIAL MAILER
                    </Button>

                    <Button
                      color='primary'
                      disabled={!values.exporterCompany || !values.michiganTitleExporterCompanyAddress}
                      onClick={() => {
                        const address = values.michiganTitleExporterCompanyAddress
                        applyOwnerFields({
                          michiganTitleOwnerName: values.exporterCompany?.name,
                          michiganTitleOwnerAddress1: formatOwnerAddress1(address),
                          michiganTitleOwnerCity: address?.city,
                          michiganTitleOwnerCounty: address?.county?.trim() || '',
                          michiganTitleOwnerCountry: address?.country,
                          michiganTitleOwnerState: address?.stateProvince,
                          michiganTitleOwnerPostalCode: address?.postalCode,
                          michiganTitleOwnerPhone: address?.phone,
                          michiganTitleOwnerIrsNumber: values.exporterCompany?.exporterIRSNumber,
                          michiganTitleOwnerDealerNumber: values.exporterCompany?.exporterDealerNumber
                        })
                      }}
                      size='small'
                      variant='text'
                    >
                      ADD AS OWNER
                    </Button>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={michiganTitlesRowLabelCellSx}>Buyer Company</TableCell>

                  <TableCell>{values.buyerCompany?.name ?? '-'}</TableCell>

                  <TableCell>
                    <TextField
                      disabled={isGettingBuyerCompanyAddresses || !values.buyerCompany}
                      fullWidth
                      InputProps={{
                        endAdornment: isGettingBuyerCompanyAddresses ? (
                          <InputAdornment position='end' sx={{ mr: 2.4 }}>
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ) : (
                          <></>
                        )
                      }}
                      onChange={e => {
                        setFieldValue(
                          'michiganTitleBuyerCompanyAddress',
                          buyerCompanyAddresses.filter(
                            buyerCompanyAddress => buyerCompanyAddress.addressGuid === e.target.value
                          )[0]
                        )
                      }}
                      margin='dense'
                      select
                      SelectProps={michiganTitlesCompactSelectProps}
                      size='small'
                      sx={michiganTitlesCompactTextFieldSx}
                      value={values.michiganTitleBuyerCompanyAddress?.addressGuid}
                    >
                      {buyerCompanyAddresses.map(buyerCompanyAddress => (
                        <MenuItem key={buyerCompanyAddress.addressGuid} value={buyerCompanyAddress.addressGuid}>
                          {formatAddress(buyerCompanyAddress, false)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell align='right' sx={michiganTitlesPartyActionsCellSx}>
                    <Button
                      disabled={!values.buyerCompany || !values.michiganTitleBuyerCompanyAddress}
                      onClick={() => {
                        setFieldValue('michiganTitleSpecialMailerName', values.buyerCompany?.name)

                        setFieldValue(
                          'michiganTitleSpecialMailerAddress1',
                          values.michiganTitleBuyerCompanyAddress?.address1 +
                            (values.michiganTitleBuyerCompanyAddress?.address2
                              ? ` ${values.michiganTitleBuyerCompanyAddress?.address2}`
                              : '')
                        )

                        setFieldValue('michiganTitleSpecialMailerCity', values.michiganTitleBuyerCompanyAddress?.city)

                        setFieldValue('michiganTitleSpecialMailerState', values.michiganTitleBuyerCompanyAddress?.stateProvince)

                        setFieldValue(
                          'michiganTitleSpecialMailerPostalCode',
                          values.michiganTitleBuyerCompanyAddress?.postalCode
                        )
                      }}
                      size='small'
                      variant='text'
                      sx={theme => ({ mr: theme.spacing(4) })}
                    >
                      ADD AS SPECIAL MAILER
                    </Button>

                    <Button
                      color='primary'
                      disabled={!values.buyerCompany || !values.michiganTitleBuyerCompanyAddress}
                      onClick={() => {
                        const address = values.michiganTitleBuyerCompanyAddress
                        applyOwnerFields({
                          michiganTitleOwnerName: values.buyerCompany?.name,
                          michiganTitleOwnerAddress1: formatOwnerAddress1(address),
                          michiganTitleOwnerCity: address?.city,
                          michiganTitleOwnerCounty: address?.county?.trim() || '',
                          michiganTitleOwnerCountry: address?.country,
                          michiganTitleOwnerState: address?.stateProvince,
                          michiganTitleOwnerPostalCode: address?.postalCode,
                          michiganTitleOwnerPhone: address?.phone
                        })
                      }}
                      size='small'
                      variant='text'
                    >
                      ADD AS OWNER
                    </Button>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={michiganTitlesRowLabelCellSx}>Title Destination Company</TableCell>

                  <TableCell>{values.titleDestinationCompany?.name}</TableCell>

                  <TableCell>
                    <TextField
                      disabled={isGettingTitleDestinationCompanyAddresses}
                      fullWidth
                      InputProps={{
                        endAdornment: isGettingTitleDestinationCompanyAddresses ? (
                          <InputAdornment position='end' sx={{ mr: 2.4 }}>
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ) : (
                          <></>
                        )
                      }}
                      onChange={e => {
                        setFieldValue(
                          'michiganTitleTitleDestinationCompanyAddress',
                          titleDestinationCompanyAddresses.filter(
                            titleDestinationCompanyAddress => titleDestinationCompanyAddress.addressGuid === e.target.value
                          )[0]
                        )
                      }}
                      margin='dense'
                      select
                      SelectProps={michiganTitlesCompactSelectProps}
                      size='small'
                      sx={michiganTitlesCompactTextFieldSx}
                      value={values.michiganTitleTitleDestinationCompanyAddress?.addressGuid}
                    >
                      {titleDestinationCompanyAddresses.map(titleDestinationCompanyAddress => (
                        <MenuItem
                          key={titleDestinationCompanyAddress.addressGuid}
                          value={titleDestinationCompanyAddress.addressGuid}
                        >
                          {formatAddress(titleDestinationCompanyAddress, false)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell align='right' sx={michiganTitlesPartyActionsCellSx}>
                    <Button
                      color='primary'
                      disabled={!values.titleDestinationCompany || !values.michiganTitleTitleDestinationCompanyAddress}
                      onClick={() => {
                        setFieldValue('michiganTitleSpecialMailerName', values.titleDestinationCompany?.name)

                        setFieldValue(
                          'michiganTitleSpecialMailerAddress1',
                          values.michiganTitleTitleDestinationCompanyAddress?.address1 +
                            (values.michiganTitleTitleDestinationCompanyAddress?.address2
                              ? ` ${values.michiganTitleTitleDestinationCompanyAddress?.address2}`
                              : '')
                        )

                        setFieldValue(
                          'michiganTitleSpecialMailerCity',
                          values.michiganTitleTitleDestinationCompanyAddress?.city
                        )

                        setFieldValue(
                          'michiganTitleSpecialMailerState',
                          values.michiganTitleTitleDestinationCompanyAddress?.stateProvince
                        )
                        setFieldValue(
                          'michiganTitleSpecialMailerPostalCode',
                          values.michiganTitleTitleDestinationCompanyAddress?.postalCode
                        )
                      }}
                      size='small'
                      variant='text'
                      sx={theme => ({ mr: theme.spacing(4) })}
                    >
                      ADD AS SPECIAL MAILER
                    </Button>

                    <Button
                      color='primary'
                      disabled={!values.titleDestinationCompany || !values.michiganTitleTitleDestinationCompanyAddress}
                      onClick={() => {
                        const address = values.michiganTitleTitleDestinationCompanyAddress
                        applyOwnerFields({
                          michiganTitleOwnerName: values.titleDestinationCompany?.name,
                          michiganTitleOwnerAddress1: formatOwnerAddress1(address),
                          michiganTitleOwnerCity: address?.city,
                          michiganTitleOwnerCounty: address?.county?.trim() || '',
                          michiganTitleOwnerCountry: address?.country,
                          michiganTitleOwnerState: address?.stateProvince,
                          michiganTitleOwnerPostalCode: address?.postalCode,
                          michiganTitleOwnerPhone: address?.phone
                        })
                      }}
                      size='small'
                      variant='text'
                    >
                      ADD AS OWNER
                    </Button>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={michiganTitlesRowLabelCellSx}>Vehicle Destination Company</TableCell>

                  <TableCell>{values.vehicleDestinationCompany?.name}</TableCell>

                  <TableCell>
                    <TextField
                      disabled={isGettingVehicleDestinationCompanyAddresses}
                      fullWidth
                      InputProps={{
                        endAdornment: isGettingVehicleDestinationCompanyAddresses ? (
                          <InputAdornment position='end' sx={{ mr: 2.4 }}>
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ) : (
                          <></>
                        )
                      }}
                      onChange={e => {
                        setFieldValue(
                          'michiganTitleVehicleDestinationCompanyAddress',
                          vehicleDestinationCompanyAddresses.filter(
                            vehicleDestinationCompanyAddress => vehicleDestinationCompanyAddress.addressGuid === e.target.value
                          )[0]
                        )
                      }}
                      margin='dense'
                      select
                      SelectProps={michiganTitlesCompactSelectProps}
                      size='small'
                      sx={michiganTitlesCompactTextFieldSx}
                      value={values.michiganTitleVehicleDestinationCompanyAddress?.addressGuid}
                    >
                      {vehicleDestinationCompanyAddresses.map(vehicleDestinationCompanyAddress => (
                        <MenuItem
                          key={vehicleDestinationCompanyAddress.addressGuid}
                          value={vehicleDestinationCompanyAddress.addressGuid}
                        >
                          {formatAddress(vehicleDestinationCompanyAddress, false)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell align='right' sx={michiganTitlesPartyActionsCellSx}>
                    <Button
                      color='primary'
                      disabled={!values.vehicleDestinationCompany || !values.michiganTitleVehicleDestinationCompanyAddress}
                      onClick={() => {
                        setFieldValue('michiganTitleSpecialMailerName', values.vehicleDestinationCompany?.name)

                        setFieldValue(
                          'michiganTitleSpecialMailerAddress1',
                          values.michiganTitleVehicleDestinationCompanyAddress?.address1 +
                            (values.michiganTitleVehicleDestinationCompanyAddress?.address2
                              ? ` ${values.michiganTitleVehicleDestinationCompanyAddress?.address2}`
                              : '')
                        )

                        setFieldValue(
                          'michiganTitleSpecialMailerCity',
                          values.michiganTitleVehicleDestinationCompanyAddress?.city
                        )

                        setFieldValue(
                          'michiganTitleSpecialMailerState',
                          values.michiganTitleVehicleDestinationCompanyAddress?.stateProvince
                        )

                        setFieldValue(
                          'michiganTitleSpecialMailerPostalCode',
                          values.michiganTitleVehicleDestinationCompanyAddress?.postalCode
                        )
                      }}
                      size='small'
                      variant='text'
                      sx={theme => ({ mr: theme.spacing(4) })}
                    >
                      ADD AS SPECIAL MAILER
                    </Button>

                    <Button
                      color='primary'
                      disabled={!values.vehicleDestinationCompany || !values.michiganTitleVehicleDestinationCompanyAddress}
                      onClick={() => {
                        const address = values.michiganTitleVehicleDestinationCompanyAddress
                        applyOwnerFields({
                          michiganTitleOwnerName: values.vehicleDestinationCompany?.name,
                          michiganTitleOwnerAddress1: formatOwnerAddress1(address),
                          michiganTitleOwnerCity: address?.city,
                          michiganTitleOwnerCounty: address?.county?.trim() || '',
                          michiganTitleOwnerCountry: address?.country,
                          michiganTitleOwnerState: address?.stateProvince,
                          michiganTitleOwnerPostalCode: address?.postalCode,
                          michiganTitleOwnerPhone: address?.phone
                        })
                      }}
                      size='small'
                      variant='text'
                    >
                      ADD AS OWNER
                    </Button>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={michiganTitlesRowLabelCellSx}>Registered Importer</TableCell>

                  <TableCell>{registeredImporterCompany?.name}</TableCell>

                  <TableCell>{registeredImporterAddress ? formatAddress(registeredImporterAddress, false) : ''}</TableCell>

                  <TableCell align='right' sx={michiganTitlesPartyActionsCellSx}>
                    <Button
                      color='primary'
                      disabled={!registeredImporterCompany || !registeredImporterAddress}
                      onClick={() => {
                        setFieldValue('michiganTitleSpecialMailerName', registeredImporterCompany?.name)

                        setFieldValue(
                          'michiganTitleSpecialMailerAddress1',
                          registeredImporterAddress?.address1 +
                            (registeredImporterAddress?.address2 ? ` ${registeredImporterAddress?.address2}` : '')
                        )

                        setFieldValue('michiganTitleSpecialMailerCity', registeredImporterAddress?.city)

                        setFieldValue('michiganTitleSpecialMailerState', registeredImporterAddress?.stateProvince)

                        setFieldValue('michiganTitleSpecialMailerPostalCode', registeredImporterAddress?.postalCode)
                      }}
                      size='small'
                      variant='text'
                      sx={theme => ({ mr: theme.spacing(4) })}
                    >
                      ADD AS SPECIAL MAILER
                    </Button>

                    <Button
                      color='primary'
                      disabled={!registeredImporterCompany || !registeredImporterAddress}
                      onClick={() => {
                        applyOwnerFields({
                          michiganTitleOwnerName: registeredImporterCompany?.name,
                          michiganTitleOwnerAddress1: formatOwnerAddress1(registeredImporterAddress),
                          michiganTitleOwnerCity: registeredImporterAddress?.city,
                          michiganTitleOwnerCounty: registeredImporterAddress?.county?.trim() || '',
                          michiganTitleOwnerCountry: registeredImporterAddress?.country,
                          michiganTitleOwnerState: registeredImporterAddress?.stateProvince,
                          michiganTitleOwnerPostalCode: registeredImporterAddress?.postalCode,
                          michiganTitleOwnerPhone: registeredImporterAddress?.phone,
                          michiganTitleOwnerIrsNumber: registeredImporterCompany?.registeredImporterIRSNumber,
                          michiganTitleOwnerDealerNumber: registeredImporterCompany?.registeredImporterNumber
                        })
                      }}
                      size='small'
                      variant='text'
                    >
                      ADD AS OWNER
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Selected Vehicle Table:**

Displays the selected vehicle from the Michigan Titles Table.

**Included Vehicle Information Table:**

The following company details are displayed in the Selected Vehicle section:

CAN Ownership

Exporter Company

- If the Exporter Company has multiple addresses, users can select another address from the dropdown menu.

Buyer Company (if one is associated with the vehicle)

- If the Buyer Company has multiple addresses, users can select another address from the dropdown menu.

Title Destination Company

- If the Title Destination Company has multiple addresses, users can select another address from the dropdown menu.

Vehicle Destination Company

- If the Vehicle Destination Company has multiple addresses, users can select another address from the dropdown menu.

Registered Importer

**Add As Owner Button:**

Clicking the "Add As Owner" button duplicates the selected company’s information into the “Selected Vehicle – Michigan Title Application Packet” section on the Michigan Titles screen.

**Add As Special Mailer Button:**

Clicking the "Add As Special Mailer" button duplicates the selected company’s information into the “Selected Vehicle – Michigan Title Application Packet” section on the Michigan Titles screen.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
